import { useEffect, useRef, useState } from "react";
import { useDataContext } from "./contexts/DataProvider";

// Both Mevo cameras via WHEP from MediaMTX
const WHEP_URLS = {
  mevo: "http://127.0.0.1:8889/mevo/whep",
  mevo2: "http://127.0.0.1:8889/mevo2/whep"
};

// Fixed height for each bar (for consistent clock height) - taller for big screens
const BAR_HEIGHT = 70; // increased height for better visibility

// Featured images from public/featured directory
const FEATURED_IMAGES = [
  "/featured/1.jpg",
  "/featured/2.jpg",
  "/featured/3.jpg",
  "/featured/4.jpg",
  "/featured/5.jpg",
  "/featured/6.jpg",
];

// Featured image popup component - shows for 10 sec, hides for 5 sec, repeats
// Auto-disables on December 28, 2025 at 10:30 AM EST
const POPUP_END_DATE = new Date("2025-12-28T10:30:00-05:00"); // EST timezone

const FeaturedImagePopup = () => {
  const [currentImage, setCurrentImage] = useState(() => 
    FEATURED_IMAGES[Math.floor(Math.random() * FEATURED_IMAGES.length)]
  );
  const [isVisible, setIsVisible] = useState(true);
  const [isExpired, setIsExpired] = useState(() => new Date() >= POPUP_END_DATE);
  const timerRef = useRef(null);

  useEffect(() => {
    // Check if already expired
    if (new Date() >= POPUP_END_DATE) {
      setIsExpired(true);
      return;
    }

    const cycle = () => {
      // Check expiry before each cycle
      if (new Date() >= POPUP_END_DATE) {
        setIsExpired(true);
        setIsVisible(false);
        return;
      }

      // Show image for 10 seconds
      setIsVisible(true);
      
      timerRef.current = setTimeout(() => {
        // Fade out and hide for 5 seconds
        setIsVisible(false);
        
        timerRef.current = setTimeout(() => {
          // Pick next random image and show again
          const randomIndex = Math.floor(Math.random() * FEATURED_IMAGES.length);
          setCurrentImage(FEATURED_IMAGES[randomIndex]);
          cycle(); // Repeat the cycle
        }, 5000); // Hidden for 5 seconds
      }, 10000); // Visible for 10 seconds
    };

    cycle();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Don't render if expired
  if (isExpired) return null;

  return (
    <div
      style={{
        position: "absolute",
        bottom: `${BAR_HEIGHT * 2 + 20}px`,
        right: 20,
        zIndex: 20,
        opacity: isVisible ? 1 : 0,
        transition: "opacity 0.3s ease-in-out",
        borderRadius: "16px",
        overflow: "hidden",
        boxShadow: "0 8px 32px rgba(0,0,0,0.6), 0 0 20px rgba(255,165,0,0.3)",
        border: "4px solid #FFD700",
        background: "#000",
        maxWidth: "450px",
        maxHeight: "400px",
      }}
    >
      <img
        src={currentImage}
        alt="Featured"
        style={{
          maxWidth: "450px",
          maxHeight: "400px",
          width: "auto",
          height: "auto",
          objectFit: "contain",
          display: "block",
        }}
      />
    </div>
  );
};

// Scrolling text bar component with Sikh-themed styling
const ScrollingBar = ({ messages, variant = "saffron" }) => {
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [animationDuration, setAnimationDuration] = useState(20);

  // Combine all messages into one string with Khanda symbol separator
  // Check multiple possible field names for the text content
  const combinedText = (messages || []).map(m => {
    // Try all possible field names
    return m.message || m.text || m.content || m.title || m.name || m.value || m.msg || "";
  }).filter(Boolean).join("   ☬   ");
  
  // Debug: log what we're receiving
  console.log(`ScrollingBar (${variant}):`, messages, "Combined:", combinedText);

  useEffect(() => {
    if (contentRef.current && combinedText) {
      const contentWidth = contentRef.current.scrollWidth;
      // Adjust speed: longer text = more time, base speed ~100px/second
      const duration = Math.max(15, contentWidth / 80);
      setAnimationDuration(duration);
    }
  }, [combinedText]);

  // Sikh-themed color schemes
  const themes = {
    // Saffron/Kesari - represents courage, sacrifice, and spirituality
    saffron: {
      background: "linear-gradient(135deg,rgb(255, 68, 0) 0%, #FF8C00 25%, #FFA500 50%, #FF8C00 75%, #FF6B00 100%)",
      borderTop: "3px solid #FFD700",
      borderBottom: "3px solid #CC5500",
      textColor: "#FFFFFF",
      textShadow: "2px 2px 4px rgba(0,0,0,0.8), 0 0 10px rgba(255,107,0,0.5)",
    },
    // Royal Blue/Neela - represents the warrior spirit and Khalsa
    blue: {
      background: "linear-gradient(135deg, #1a237e 0%, #283593 25%, #3949ab 50%, #283593 75%, #1a237e 100%)",
      borderTop: "3px solid #5c6bc0",
      borderBottom: "3px solid #0d1442",
      textColor: "#FFFFFF",
      textShadow: "2px 2px 4px rgba(0,0,0,0.8), 0 0 10px rgba(57,73,171,0.5)",
    }
  };

  const theme = themes[variant] || themes.saffron;

  return (
    <div
      ref={containerRef}
      style={{
        overflow: "hidden",
        whiteSpace: "nowrap",
        background: theme.background,
        borderTop: theme.borderTop,
        borderBottom: theme.borderBottom,
        padding: "16px 0",
        width: "100%",
        height: `${BAR_HEIGHT}px`,
        boxSizing: "border-box",
        boxShadow: "0 4px 15px rgba(0,0,0,0.4)",
        display: "flex",
        alignItems: "center",
      }}
    >
      {combinedText ? (
        <div
          ref={contentRef}
          style={{
            display: "inline-block",
            paddingLeft: "100%",
            animation: `scroll-left ${animationDuration}s linear infinite`,
            color: theme.textColor,
            fontSize: "2.5rem",
            fontWeight: "700",
            fontFamily: "'Noto Sans Gurmukhi', 'Segoe UI', Arial, sans-serif",
            textShadow: theme.textShadow,
            letterSpacing: "1px",
          }}
        >
          {combinedText}
        </div>
      ) : (
        <div style={{ 
          color: "rgba(255,255,255,0.3)", 
          fontSize: "2rem", 
          textAlign: "center",
          width: "100%",
        }}>
          ☬ ਵਾਹਿਗੁਰੂ ☬
        </div>
      )}
      <style>
        {`
          @keyframes scroll-left {
            0% {
              transform: translateX(0);
            }
            100% {
              transform: translateX(-100%);
            }
          }
        `}
      </style>
    </div>
  );
};

// Live clock component with Sikh-themed styling
const LiveClock = () => {
  const [time, setTime] = useState(new Date());

  useEffect(() => {
    const timer = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  const formatTime = (date) => {
    return date.toLocaleTimeString("en-US", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: true,
    });
  };

  const formatDay = (date) => {
    return date.toLocaleDateString("en-US", {
      weekday: "long",
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  return (
    <div
      style={{
        background: "linear-gradient(180deg, #1a1a2e 0%, #16213e 50%, #0f0f23 100%)",
        color: "#FFD700",
        padding: "16px 30px 20px 30px",
        fontSize: "2.5rem",
        fontWeight: "bold",
        fontFamily: "'Segoe UI', Arial, sans-serif",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "flex-start",
        minWidth: "240px",
        height: `${BAR_HEIGHT * 2}px`,
        boxSizing: "border-box",
        borderLeft: "4px solid #FF6B00",
        textShadow: "0 0 10px rgba(255,215,0,0.5), 2px 2px 4px rgba(0,0,0,0.8)",
        boxShadow: "inset 0 0 20px rgba(255,215,0,0.1)",
        flexShrink: 0,
        paddingTop: "12px",
      }}
    >
      <div style={{ marginBottom: "8px" }}>
        {formatTime(time)}
      </div>
      <div style={{
        fontSize: "1.4rem",
        fontWeight: "600",
        color: "rgba(255, 215, 0, 0.9)",
        textAlign: "center",
        lineHeight: "1.3",
      }}>
        {formatDay(time)}
      </div>
    </div>
  );
};

const VideoStream = ({ currentSetting }) => {
  const videoRef = useRef(null);
  const { scrollboard } = useDataContext();
  
  // Filter scrollboard by position
  const topMessages = (scrollboard || []).filter(m => m.position === "top" && m.isActive !== false);
  const bottomMessages = (scrollboard || []).filter(m => (m.position === "bottom" || !m.position) && m.isActive !== false);

  const [currentDeviceKey, setCurrentDeviceKey] = useState(null);
  // Track which stream is actually being displayed (for correct rotation)
  const [activeStreamKey, setActiveStreamKey] = useState(null);
  const switchIntervalRef = useRef(null);

  // Mevo streams kept alive (both cameras)
  const mevoStreamRef = useRef(null);
  const mevo2StreamRef = useRef(null);

  // Mevo WebRTC state (persistent for both)
  const pcMevoRef = useRef(null);
  const pcMevo2Ref = useRef(null);
  const retryTimerMevoRef = useRef(null);
  const retryTimerMevo2Ref = useRef(null);
  const watchdogRef = useRef(null);

  // Track current key inside callbacks
  const currentKeyRef = useRef(null);
  useEffect(() => { currentKeyRef.current = currentDeviceKey; }, [currentDeviceKey]);

  // Initialize both Mevo connections on mount
  useEffect(() => {
    // Prewarm both Mevo connections
    ensureMevoPeer("mevo", false).catch(() => {});
    ensureMevoPeer("mevo2", false).catch(() => {});

    return () => cleanupAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ---------- Mevo via WHEP (persistent) ----------
  async function ensureMevoPeer(which = "mevo", attachNow = false) {
    const isMevo1 = which === "mevo";
    const streamRef = isMevo1 ? mevoStreamRef : mevo2StreamRef;
    const pcRef = isMevo1 ? pcMevoRef : pcMevo2Ref;
    const whepUrl = WHEP_URLS[which];

    // If we already have a live stream, optionally attach it and return.
    if (streamRef.current) {
      if (attachNow && videoRef.current && currentKeyRef.current === which) {
        videoRef.current.srcObject = streamRef.current;
        setActiveStreamKey(which); // Track which stream is actually displayed
        await videoRef.current.play().catch(() => {});
      }
      return;
    }

    // If we have a PC already but no track yet, do nothing here; ontrack will attach when it arrives.
    if (!pcRef.current) {
      const pc = new RTCPeerConnection();
      pcRef.current = pc;

      pc.addTransceiver("video", { direction: "recvonly" });
      pc.addTransceiver("audio", { direction: "recvonly" });

      pc.ontrack = (e) => {
        streamRef.current = e.streams[0];
        // Attach only if this camera is the active view or if attachNow was requested
        if (videoRef.current && (attachNow || currentKeyRef.current === which)) {
          videoRef.current.srcObject = streamRef.current;
          setActiveStreamKey(which); // Track which stream is actually displayed
          videoRef.current.play().catch(() => {});
        }
      };

      pc.onconnectionstatechange = () => {
        const s = pc.connectionState;
        if (s === "failed" || s === "disconnected" || s === "closed") {
          scheduleReconnect(which);
        }
      };

      try {
        const offer = await pc.createOffer();
        await pc.setLocalDescription(offer);
        const res = await fetch(whepUrl, {
          method: "POST",
          headers: { "Content-Type": "application/sdp" },
          body: offer.sdp
        });
        if (!res.ok) {
          const body = await res.text().catch(() => "");
          throw new Error(`WHEP ${res.status} ${body}`);
        }
        const answer = await res.text();
        await pc.setRemoteDescription({ type: "answer", sdp: answer });
        startWatchdog();
      } catch (err) {
        console.error(`WHEP attach failed for ${which}:`, err);
        scheduleReconnect(which);
      }
    }
  }

  function scheduleReconnect(which = "mevo", delay = 1500) {
    const isMevo1 = which === "mevo";
    const retryTimerRef = isMevo1 ? retryTimerMevoRef : retryTimerMevo2Ref;
    const pcRef = isMevo1 ? pcMevoRef : pcMevo2Ref;
    const streamRef = isMevo1 ? mevoStreamRef : mevo2StreamRef;

    clearTimeout(retryTimerRef.current);
    retryTimerRef.current = setTimeout(() => {
      // clear stale state; let ensureMevoPeer recreate/reattach
      if (pcRef.current && pcRef.current.connectionState !== "connected") {
        try { pcRef.current.close(); } catch {}
        pcRef.current = null;
        streamRef.current = null;
      }
      ensureMevoPeer(which, currentKeyRef.current === which)
        .catch(() => scheduleReconnect(which, Math.min(delay * 1.5, 10000)));
    }, delay);
  }

  function startWatchdog() {
    clearInterval(watchdogRef.current);
    watchdogRef.current = setInterval(() => {
      // Check mevo
      const pcMevo = pcMevoRef.current;
      if (!pcMevo || ["failed", "disconnected", "closed"].includes(pcMevo.connectionState)) {
        ensureMevoPeer("mevo", currentKeyRef.current === "mevo").catch(() => {});
      }
      // Check mevo2
      const pcMevo2 = pcMevo2Ref.current;
      if (!pcMevo2 || ["failed", "disconnected", "closed"].includes(pcMevo2.connectionState)) {
        ensureMevoPeer("mevo2", currentKeyRef.current === "mevo2").catch(() => {});
      }
    }, 15000);
  }

  // ---------- Cleanup ----------
  function cleanupAll() {
    clearTimeout(retryTimerMevoRef.current);
    clearTimeout(retryTimerMevo2Ref.current);
    clearInterval(watchdogRef.current);

    if (videoRef.current && videoRef.current.srcObject) {
      try { videoRef.current.srcObject.getTracks().forEach(t => t.stop()); } catch {}
      videoRef.current.srcObject = null;
    }
    if (pcMevoRef.current) {
      try { pcMevoRef.current.close(); } catch {}
      pcMevoRef.current = null;
    }
    if (pcMevo2Ref.current) {
      try { pcMevo2Ref.current.close(); } catch {}
      pcMevo2Ref.current = null;
    }
    mevoStreamRef.current = null;
    mevo2StreamRef.current = null;
  }

  // Retry when network returns or tab becomes visible
  useEffect(() => {
    const onOnline = () => {
      if (currentKeyRef.current === "mevo") ensureMevoPeer("mevo", true).catch(() => {});
      if (currentKeyRef.current === "mevo2") ensureMevoPeer("mevo2", true).catch(() => {});
    };
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        if (currentKeyRef.current === "mevo") ensureMevoPeer("mevo", true).catch(() => {});
        if (currentKeyRef.current === "mevo2") ensureMevoPeer("mevo2", true).catch(() => {});
      }
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, []);

  // ---------- Switching logic ----------
  useEffect(() => {
    if (switchIntervalRef.current) {
      clearTimeout(switchIntervalRef.current);
      switchIntervalRef.current = null;
    }

    if (currentSetting === "live") {
      // Live mode: show mevo only
      setCurrentDeviceKey("mevo");
      ensureMevoPeer("mevo", true).catch(() => {});
    } else if (currentSetting === "kirtan") {
      // Kirtan mode: switch between mevo2 and mevo
      // Prewarm both connections
      ensureMevoPeer("mevo", false).catch(() => {});
      ensureMevoPeer("mevo2", false).catch(() => {});

      let state = "mevo2";
      setCurrentDeviceKey("mevo2");
      const loop = () => {
        state = state === "mevo2" ? "mevo" : "mevo2";
        setCurrentDeviceKey(state);
        // mevo2 shows for 25s, mevo shows for 7s
        const timeout = state === "mevo2" ? 25000 : 7000;
        switchIntervalRef.current = setTimeout(loop, timeout);
      };
      switchIntervalRef.current = setTimeout(loop, 20000);
    } else {
      setCurrentDeviceKey(null);
      cleanupAll();
    }

    return () => switchIntervalRef.current && clearTimeout(switchIntervalRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSetting]);

  // Attach when device changes
  useEffect(() => {
    (async () => {
      const key = currentDeviceKey;
      if (!key) return;

      if (key === "mevo") {
        await ensureMevoPeer("mevo", true);
      } else if (key === "mevo2") {
        await ensureMevoPeer("mevo2", true);
      }
    })().catch(console.error);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDeviceKey]);

  // Show scroll bars when scrollboard has content
  const hasScrollContent = scrollboard !== undefined && scrollboard.length > 0;

  return (
    <div style={{ width: "100%", height: "100%", backgroundColor: "#000", display: "flex", flexDirection: "column", position: "relative" }}>
      {/* Video area - takes remaining space after bars */}
      <div style={{ flex: 1, position: "relative", overflow: "hidden", minHeight: 0 }}>
        {currentDeviceKey ? (
          <video
            ref={videoRef}
            autoPlay
            muted
            playsInline
            style={{ width: "100%", height: "100%", objectFit: "cover", transform: activeStreamKey === "mevo" ? "rotate(180deg)" : "none" }}
          />
        ) : null}

        {/* Logo overlay - Top Left */}
        <div
          style={{
            position: "absolute",
            top: 20,
            left: 20,
            zIndex: 15,
            background: "rgba(255,255,255,0.9)",
            borderRadius: "12px",
            padding: "8px",
            boxShadow: "0 4px 20px rgba(0,0,0,0.4)",
          }}
        >
          <img
            src="https://brantsikhs.com/logo.png"
            alt="Sikh Association of Brantford"
            style={{
              width: "100px",
              height: "100px",
              objectFit: "contain",
            }}
          />
        </div>
      </div>

      {/* Featured image popup - bottom right, above the clock */}
      <FeaturedImagePopup />

      {/* Bottom bar with scrolling text and time - NOT overlayed, video shrinks to fit */}
      {hasScrollContent && (
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            alignItems: "stretch",
            flexShrink: 0,
            height: `${BAR_HEIGHT * 2}px`,
          }}
        >
          {/* Left side: Scrolling bars */}
          <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
            {/* Top position messages - Saffron/Kesari theme (Orange bar) */}
            <ScrollingBar
              messages={topMessages}
              variant="saffron"
            />
            {/* Bottom position messages - Royal Blue/Neela theme (Blue bar) */}
            <ScrollingBar
              messages={bottomMessages}
              variant="blue"
            />
          </div>

          {/* Right side: Time display */}
          <LiveClock />
        </div>
      )}
    </div>
  );
};

export default VideoStream;
